import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, Phone, Settings, Bot, CheckCircle, XCircle, Loader, Sparkles } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import {
  useAllCompanies,
  useCreateCompany,
  useUpdateCompany,
  useUpdateCompanyStatus,
  useDeleteCompany
} from '@/lib/hooks/useAIVoiceAgents';
import { useActiveQueues } from '@/lib/hooks/useQueues';
import type { CompanyConfig } from '@/lib/repositories/CompanyConfigRepository';
import type { CallQueue } from '@/lib/repositories/QueueRepository';

export default function CompaniesPage() {
  const { theme } = useTheme();
  
  // Use hooks for data fetching and mutations
  const { data: companies = [], isLoading: loading } = useAllCompanies();
  const { data: queues = [] } = useActiveQueues();
  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany();
  const updateStatusMutation = useUpdateCompanyStatus();
  const deleteCompanyMutation = useDeleteCompany();
  
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyConfig | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    queue_id: null as string | null,
    disclaimer_voice_id: '',
    agent_voice_id: '',
    elevenlabs_agent_id: '',
    agent_id: '',
    knowledge_base_ids: [''],
    rules: {},
    system_prompt: '',
    agent_personality: {
      tone: 'professional',
      pace: 'moderate',
      empathy_level: 'high'
    },
    conversation_starters: [''],
    disclaimer_audio_url: ''
  });
  const [creatingAgent, setCreatingAgent] = useState<string | null>(null);
  const [registeringTools, setRegisteringTools] = useState<string | null>(null);

  // ElevenLabs convai_write permission check (via edge function)
  const [convaiWrite, setConvaiWrite] = useState<boolean | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('elevenlabs-permissions-check', { body: {} });
        if (!mounted) return;
        if (error) {
          console.error('permissions-check error:', error);
          setConvaiWrite(false);
        } else {
          setConvaiWrite(Boolean((data as any)?.convai_write));
        }
      } catch (e) {
        console.warn('permissions-check failed:', e);
        if (mounted) setConvaiWrite(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function saveCompany() {
    try {
      const companyData = {
        ...formData,
        knowledge_base_ids: formData.knowledge_base_ids.filter(id => id.trim() !== '')
      };

      if (editingCompany) {
        await updateCompanyMutation.mutateAsync({ id: editingCompany.id, data: companyData });
      } else {
        await createCompanyMutation.mutateAsync(companyData);
      }
      setShowAddForm(false);
      setEditingCompany(null);
      resetForm();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  }

  async function toggleCompanyStatus(id: string, currentStatus: boolean) {
    try {
      await updateStatusMutation.mutateAsync({ id, isActive: !currentStatus });
    } catch (error) {
      console.error('Error toggling company status:', error);
      alert('Failed to update company status');
    }
  }

  async function deleteCompany(id: string) {
    if (!confirm('Are you sure you want to delete this company?')) return;

    try {
      await deleteCompanyMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      company_name: '',
      queue_id: null,
      disclaimer_voice_id: '',
      agent_voice_id: '',
      elevenlabs_agent_id: '',
      agent_id: '',
      knowledge_base_ids: [''],
      rules: {},
      system_prompt: '',
      agent_personality: {
        tone: 'professional',
        pace: 'moderate',
        empathy_level: 'high'
      },
      conversation_starters: [''],
      disclaimer_audio_url: ''
    });
  }

  function startEdit(company: CompanyConfig) {
    setEditingCompany(company);
    setFormData({
      name: company.name || company.company_name,
      company_name: company.company_name || company.name,
      queue_id: company.queue_id,
      disclaimer_voice_id: company.disclaimer_voice_id || '',
      agent_voice_id: company.agent_voice_id || '',
      elevenlabs_agent_id: company.elevenlabs_agent_id || company.agent_id || '',
      agent_id: company.agent_id || company.elevenlabs_agent_id || '',
      knowledge_base_ids: company.knowledge_base_ids || [''],
      rules: company.rules || {},
      system_prompt: company.system_prompt || '',
      agent_personality: company.agent_personality || {
        tone: 'professional',
        pace: 'moderate',
        empathy_level: 'high'
      },
      conversation_starters: company.conversation_starters || [''],
      disclaimer_audio_url: company.disclaimer_audio_url || ''
    });
    setShowAddForm(true);
  }

  function addConversationStarter() {
    setFormData(prev => ({
      ...prev,
      conversation_starters: [...prev.conversation_starters, '']
    }));
  }

  function updateConversationStarter(index: number, value: string) {
    setFormData(prev => ({
      ...prev,
      conversation_starters: prev.conversation_starters.map((starter, i) => i === index ? value : starter)
    }));
  }

  function removeConversationStarter(index: number) {
    setFormData(prev => ({
      ...prev,
      conversation_starters: prev.conversation_starters.filter((_, i) => i !== index)
    }));
  }

  async function createElevenLabsAgent(companyId: string, companyName: string) {
    try {
      setCreatingAgent(companyId);
      
      const { data, error } = await supabase.functions.invoke('elevenlabs-agent-manager', {
        body: {
          action: 'create',
          company_id: companyId,
          company_name: companyName
        }
      });

      if (error) throw error;

      alert(`✅ ElevenLabs agent created successfully!\nAgent ID: ${data.agent_id}`);
      // Cache will be automatically invalidated by React Query
    } catch (error) {
      console.error('❌ Failed to create agent:', error);
      alert(`❌ Failed to create agent: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreatingAgent(null);
    }
  }

  async function syncAgentToElevenLabs(companyId: string) {
    try {
      setCreatingAgent(companyId);
      
      const { data, error } = await supabase.functions.invoke('elevenlabs-agent-manager', {
        body: {
          action: 'sync',
          company_id: companyId
        }
      });

      if (error) throw error;

      alert('✅ Agent synced to ElevenLabs successfully!');
      // Cache will be automatically invalidated by React Query
    } catch (error) {
      console.error('❌ Failed to sync agent:', error);
      alert(`❌ Failed to sync agent: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreatingAgent(null);
    }
  }

  async function reRegisterTools(companyId: string, agentId?: string) {
    try {
      if (!agentId) {
        alert('No ElevenLabs agent ID found for this company.');
        return;
      }
      setRegisteringTools(companyId);
      const { data, error } = await supabase.functions.invoke('elevenlabs-tools-registry', {
        body: {
          action: 'register',
          agentId
        }
      });
      if (error) throw error;

      alert('✅ Tools re-registered on ElevenLabs agent successfully!');
    } catch (error) {
      console.error('❌ Failed to re-register tools:', error);
      alert(`❌ Failed to re-register tools: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setRegisteringTools(null);
    }
  }

  const getStatusStyle = (isActive: boolean) => {
    if (isActive) {
      return {
        backgroundColor: theme.colors.successBg,
        color: theme.colors.successText,
        borderColor: theme.colors.successBorder
      };
    }
    return {
      backgroundColor: theme.colors.dangerBg,
      color: theme.colors.dangerText,
      borderColor: theme.colors.dangerBorder
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: theme.colors.primaryColor }}
        />
      </div>
    );
  }

  // Filter companies based on status
  const filteredCompanies = companies.filter(company => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return company.is_active;
    if (statusFilter === 'inactive') return !company.is_active;
    return true;
  });

  return (
    <div className="p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            AI Voice Agent Companies
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Manage companies and their AI voice agent configurations
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 rounded-lg flex items-center space-x-2"
          style={{
            backgroundColor: theme.colors.primaryColor,
            color: '#ffffff'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
        >
          <Plus className="w-4 h-4" />
          <span>Add Company</span>
        </button>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900">
        <p className="text-sm">
          About this page: Create and manage companies that have AI voice agents. This page writes to the
          <span className="font-mono"> company_configs</span> table and provides actions to:
          - create/update company configs (voice IDs, queues, prompt)
          - create/sync ElevenLabs agents via edge functions
          - re-register tools to the ElevenLabs agent.
        </p>
        <p className="text-xs mt-1">
          Workflow: Seed knowledge/scripts ➜ compile system prompt at
          <a href="/ai-voice-agents/compiler" className="underline font-medium"> Scripts Compiler</a> ➜ Save to company ➜ Sync to ElevenLabs.
        </p>
      </div>

      {/* ElevenLabs convai_write notice */}
      {convaiWrite === false && (
        <div className="mb-6 p-4 rounded-lg border bg-amber-50 text-amber-900">
          <p className="text-sm">
            ElevenLabs API key configured in Supabase is missing convai_write permission. Creating/syncing agents and re-registering tools will fail until this is updated.
          </p>
          <p className="text-xs mt-2">
            Update Supabase project secret ELEVEN_LABS_API_KEY with a service account that includes convai_write. Verify locally:
          </p>
          <pre className="text-xs mt-2 p-2 rounded overflow-auto"
               style={{ backgroundColor: theme.colors.bgPage, border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}>
{`ELEVEN_LABS_API_KEY="sk_..." node scripts/verify-elevenlabs-key.mjs`}
          </pre>
        </div>
      )}

      {/* Status Filter */}
      <div className="mb-6 flex items-center space-x-2">
        <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
          Filter:
        </span>
        <div className="flex space-x-2">
          <button
            onClick={() => setStatusFilter('all')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: statusFilter === 'all' ? theme.colors.primaryColor : theme.colors.bgCard,
              color: statusFilter === 'all' ? '#ffffff' : theme.colors.textPrimary,
              border: `1px solid ${statusFilter === 'all' ? theme.colors.primaryColor : theme.colors.borderColor}`
            }}
            onMouseEnter={(e) => {
              if (statusFilter !== 'all') {
                e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }
            }}
            onMouseLeave={(e) => {
              if (statusFilter !== 'all') {
                e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }
            }}
          >
            All ({companies.length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: statusFilter === 'active' ? theme.colors.successBg : theme.colors.bgCard,
              color: statusFilter === 'active' ? theme.colors.successText : theme.colors.textPrimary,
              border: `1px solid ${statusFilter === 'active' ? theme.colors.successBorder : theme.colors.borderColor}`
            }}
            onMouseEnter={(e) => {
              if (statusFilter !== 'active') {
                e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }
            }}
            onMouseLeave={(e) => {
              if (statusFilter !== 'active') {
                e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }
            }}
          >
            Active ({companies.filter(c => c.is_active).length})
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: statusFilter === 'inactive' ? theme.colors.dangerBg : theme.colors.bgCard,
              color: statusFilter === 'inactive' ? theme.colors.dangerText : theme.colors.textPrimary,
              border: `1px solid ${statusFilter === 'inactive' ? theme.colors.dangerBorder : theme.colors.borderColor}`
            }}
            onMouseEnter={(e) => {
              if (statusFilter !== 'inactive') {
                e.currentTarget.style.backgroundColor = theme.colors.bgHover;
              }
            }}
            onMouseLeave={(e) => {
              if (statusFilter !== 'inactive') {
                e.currentTarget.style.backgroundColor = theme.colors.bgCard;
              }
            }}
          >
            Inactive ({companies.filter(c => !c.is_active).length})
          </button>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => (
          <div 
            key={company.id} 
            className="rounded-lg p-6"
            style={{
              backgroundColor: theme.colors.bgCard,
              border: `1px solid ${theme.colors.borderColor}`
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                  {company.name}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <button
                    onClick={() => toggleCompanyStatus(company.id, company.is_active)}
                    className="inline-flex px-2 py-1 text-xs rounded-full transition-all cursor-pointer"
                    style={getStatusStyle(company.is_active)}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    title={company.is_active ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {company.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(company)}
                  style={{ color: theme.colors.textTertiary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                  title="Edit company"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteCompany(company.id)}
                  style={{ color: theme.colors.textTertiary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.dangerText}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                  title="Delete company"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  Call Queue
                </p>
                <div className="mt-1">
                  {company.queue_id ? (
                    <span 
                      className="inline-flex items-center px-2 py-1 text-xs rounded"
                      style={{
                        backgroundColor: theme.colors.bgHover,
                        color: theme.colors.textPrimary
                      }}
                    >
                      <Phone className="w-3 h-3 mr-1" />
                      {queues.find(q => q.id === company.queue_id)?.company_name || 'Unknown Queue'}
                    </span>
                  ) : (
                    <span 
                      className="text-xs"
                      style={{ color: theme.colors.textTertiary }}
                    >
                      No queue assigned
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                    ElevenLabs Agent
                  </p>
                  {company.agent_id || company.elevenlabs_agent_id ? (
                    <CheckCircle className="w-4 h-4" style={{ color: theme.colors.successText }} />
                  ) : (
                    <XCircle className="w-4 h-4" style={{ color: theme.colors.dangerText }} />
                  )}
                </div>
                {company.agent_id || company.elevenlabs_agent_id ? (
                  <p 
                    className="text-sm font-mono truncate"
                    style={{ color: theme.colors.textPrimary }}
                  >
                    {company.agent_id || company.elevenlabs_agent_id}
                  </p>
                ) : (
                  <button
                    onClick={() => createElevenLabsAgent(company.id, company.company_name || company.name)}
                    disabled={creatingAgent === company.id || convaiWrite === false}
                    className="mt-1 text-sm flex items-center space-x-1 disabled:opacity-50"
                    style={{ color: theme.colors.primaryColor }}
                    onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryHoverColor)}
                    onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryColor)}
                  >
                    {creatingAgent === company.id ? (
                      <>
                        <Loader className="w-3 h-3 animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <>
                        <Bot className="w-3 h-3" />
                        <span>Create Agent</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div>
                <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  Knowledge Base
                </p>
                <p className="text-sm" style={{ color: theme.colors.textPrimary }}>
                  {company.knowledge_base_ids?.length || 0} documents
                </p>
              </div>

              <div>
                <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  MCP Token
                </p>
                <div className="mt-1">
                  {company.mcp_token ? (
                    <div className="space-y-1">
                      <span
                        className="inline-flex items-center px-2 py-1 text-xs rounded"
                        style={{
                          backgroundColor: theme.colors.successBg,
                          color: theme.colors.successText
                        }}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Configured
                      </span>
                      <p
                        className="text-xs font-mono truncate"
                        style={{ color: theme.colors.textTertiary }}
                      >
                        {company.mcp_token}
                      </p>
                    </div>
                  ) : (
                    <span
                      className="inline-flex items-center px-2 py-1 text-xs rounded"
                      style={{
                        backgroundColor: theme.colors.dangerBg,
                        color: theme.colors.dangerText
                      }}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Not Set
                    </span>
                  )}
                </div>
              </div>

              {(company.agent_id || company.elevenlabs_agent_id) && (
                <div className="pt-2" style={{ borderTop: `1px solid ${theme.colors.borderColor}` }}>
                  <div className="space-y-2">
                    <button
                      onClick={() => syncAgentToElevenLabs(company.id)}
                      disabled={creatingAgent === company.id || convaiWrite === false}
                      className="w-full text-sm flex items-center justify-center space-x-1 disabled:opacity-50"
                      style={{ color: theme.colors.primaryColor }}
                      onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryHoverColor)}
                      onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryColor)}
                    >
                      {creatingAgent === company.id ? (
                        <>
                          <Loader className="w-3 h-3 animate-spin" />
                          <span>Syncing...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          <span>Sync to ElevenLabs</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => reRegisterTools(company.id, (company.agent_id || company.elevenlabs_agent_id) as string)}
                      disabled={registeringTools === company.id || convaiWrite === false}
                      className="w-full text-sm flex items-center justify-center space-x-1 disabled:opacity-50"
                      style={{ color: theme.colors.primaryColor }}
                      onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryHoverColor)}
                      onMouseLeave={(e) => !e.currentTarget.disabled && (e.currentTarget.style.color = theme.colors.primaryColor)}
                    >
                      {registeringTools === company.id ? (
                        <>
                          <Loader className="w-3 h-3 animate-spin" />
                          <span>Re-registering...</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          <span>Re-register Tools</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredCompanies.length === 0 && companies.length > 0 && (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.textPrimary }}>
            No {statusFilter} companies found
          </h3>
          <p className="mb-4" style={{ color: theme.colors.textSecondary }}>
            Try selecting a different filter option.
          </p>
        </div>
      )}

      {companies.length === 0 && (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.textPrimary }}>
            No companies configured
          </h3>
          <p className="mb-4" style={{ color: theme.colors.textSecondary }}>
            Get started by adding your first company configuration.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 rounded-lg"
            style={{
              backgroundColor: theme.colors.primaryColor,
              color: '#ffffff'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
          >
            Add Company
          </button>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.colors.bgCard }}
          >
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
                {editingCompany ? 'Edit Company' : 'Add Company'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                    placeholder="Hales AC"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Call Queue
                  </label>
                  <select
                    value={formData.queue_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, queue_id: e.target.value || null }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                  >
                    <option value="">Select a queue...</option>
                    {queues
                      .filter(q => q.is_active)
                      .map(queue => (
                        <option key={queue.id} value={queue.id}>
                          {queue.company_name}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
                    Select the call queue that will route calls to this AI agent. Phone numbers are assigned to queues in Queue Management.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Disclaimer Voice ID
                    </label>
                    <input
                      type="text"
                      value={formData.disclaimer_voice_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, disclaimer_voice_id: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="elevenlabs-voice-id"
                    />
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Agent Voice ID
                    </label>
                    <input
                      type="text"
                      value={formData.agent_voice_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, agent_voice_id: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.textPrimary
                      }}
                      placeholder="elevenlabs-voice-id"
                    />
                  </div>
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    ElevenLabs Agent ID
                  </label>
                  <input
                    type="text"
                    value={formData.elevenlabs_agent_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, elevenlabs_agent_id: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                    placeholder="your-elevenlabs-agent-id"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Knowledge Base Document IDs
                  </label>
                  <input
                    type="text"
                    value={formData.knowledge_base_ids.join(', ')}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      knowledge_base_ids: e.target.value.split(',').map(id => id.trim()).filter(id => id !== '')
                    }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                    placeholder="doc-id-1, doc-id-2, doc-id-3"
                  />
                </div>

                {/* System Prompt */}
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    System Prompt (Agent Instructions)
                  </label>
                  <textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                    rows={6}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2 font-mono text-sm"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                    placeholder="You are a helpful AI assistant for [Company Name]. Your role is to..."
                  />
                  <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
                    Define how the AI agent should behave, what information it should provide, and how it should handle different scenarios.
                  </p>
                </div>

                {/* Agent Personality */}
                <div className="pt-4" style={{ borderTop: `1px solid ${theme.colors.borderColor}` }}>
                  <h3 className="text-sm font-semibold mb-3" style={{ color: theme.colors.textPrimary }}>
                    Agent Personality
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        Tone
                      </label>
                      <select
                        value={formData.agent_personality.tone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          agent_personality: { ...prev.agent_personality, tone: e.target.value }
                        }))}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          border: `1px solid ${theme.colors.borderColor}`,
                          color: theme.colors.textPrimary
                        }}
                      >
                        <option value="professional">Professional</option>
                        <option value="friendly">Friendly</option>
                        <option value="casual">Casual</option>
                        <option value="formal">Formal</option>
                      </select>
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        Pace
                      </label>
                      <select
                        value={formData.agent_personality.pace}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          agent_personality: { ...prev.agent_personality, pace: e.target.value }
                        }))}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          border: `1px solid ${theme.colors.borderColor}`,
                          color: theme.colors.textPrimary
                        }}
                      >
                        <option value="slow">Slow</option>
                        <option value="moderate">Moderate</option>
                        <option value="fast">Fast</option>
                      </select>
                    </div>
                    <div>
                      <label 
                        className="block text-sm font-medium mb-1"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        Empathy Level
                      </label>
                      <select
                        value={formData.agent_personality.empathy_level}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          agent_personality: { ...prev.agent_personality, empathy_level: e.target.value }
                        }))}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          border: `1px solid ${theme.colors.borderColor}`,
                          color: theme.colors.textPrimary
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Conversation Starters */}
                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Conversation Starters
                  </label>
                  <div className="space-y-2">
                    {formData.conversation_starters.map((starter, index) => (
                      <div key={index} className="flex space-x-2">
                        <input
                          type="text"
                          value={starter}
                          onChange={(e) => updateConversationStarter(index, e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg focus:ring-2"
                          style={{
                            backgroundColor: theme.colors.bgPage,
                            border: `1px solid ${theme.colors.borderColor}`,
                            color: theme.colors.textPrimary
                          }}
                          placeholder="Thank you for calling [Company Name], how can I help you today?"
                        />
                        {formData.conversation_starters.length > 1 && (
                          <button
                            onClick={() => removeConversationStarter(index)}
                            className="px-3 py-1"
                            style={{ color: theme.colors.dangerText }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={addConversationStarter}
                      className="text-sm"
                      style={{ color: theme.colors.primaryColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primaryHoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primaryColor}
                    >
                      + Add Conversation Starter
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: theme.colors.textTertiary }}>
                    Opening phrases the AI agent can use to greet callers.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingCompany(null);
                    resetForm();
                  }}
                  className="px-4 py-2 rounded-lg"
                  style={{
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: theme.colors.bgCard
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgCard}
                >
                  Cancel
                </button>
                <button
                  onClick={saveCompany}
                  className="px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: theme.colors.primaryColor,
                    color: '#ffffff'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
                >
                  {editingCompany ? 'Update' : 'Create'} Company
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
